import { LitElement, html, css, classMap } from 'lit-all'
class NovaInputAnimationWrapper extends LitElement {
  static properties = {
    animation: {},
    focused: {
      type: Boolean,
      reflect: true,
    },
    filled: {
      type: Boolean,
      reflect: true,
    },
    disabled: {
      type: Boolean,
      reflect: true,
    },
    error: {
      type: Boolean,
      reflect: true,
    },
    icon: {
      type: String,
    },
    clearable: {
      type: Boolean,
      reflect: true,
    },
    validate: {
      type: Boolean,
      reflect: true,
    },
    success: {
      type: Boolean,
      reflect: true,
    },
    revealPassword: {
      type: Boolean,
      reflect: true,
      attribute: 'reveal-password',
    },
    resize: {
      type: Boolean,
      reflect: true,
    },
    label: {},
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()

    this.$select = this.querySelector(`select[slot='input']`)
    this.$select && this.preselectSelectValue()

    this.checkIfPreFilled()

    if (this.$select && this.label !== 'stacked') {
      this.filled = true
    }
  }

  firstUpdated() {
    this.checkIfPreFilled()

    if (this.input && this.input.disabled) {
      this.disabled = true
    }

    if (this.errorSlot) {
      this.error = true
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('disabled')) {
      this.input.disabled = this.disabled
    }
  }

  checkIfPreFilled(tries = 6) {
    if (this.filled) return
    if (this.input?.value && !this.$select) {
      this.filled = true
    } else if (tries > 0) {
      setTimeout(() => {
        this.checkIfPreFilled(tries - 1)
      }, 200)
    }
  }

  preselectSelectValue() {
    if (this.$select?.dataset?.default) {
      this.$select.value = this.$select?.dataset?.default
    }
  }

  onChange(e) {
    if (this.validate) {
      this.handleValidation()
    }

    if (this.resize) {
      this.handleAutoResize(e)
    }

    if (this.animation === 'none' || this.label === 'stacked') {
      return
    }
    this.filled = !!e.target.value
  }

  onFocus(e) {
    if (this.animation === 'none') {
      return
    }
    this.focused = true
  }

  onFocusOut(e) {
    if (this.animation === 'none') {
      return
    }
    this.focused = false
  }

  onAutoFill({ animationName }) {
    if (animationName === 'onAutoFillStart') {
      this.filled = true
    }
  }

  onIconClick(e) {
    e.preventDefault()
    if (this.clearable) {
      this.handleClear()
    }

    if (this.revealPassword) {
      this.handleRevealPassword()
    }
  }

  handleClear() {
    this.input.value = ''
    this.filled = false
    this.input.focus()
    this.input.dispatchEvent(new Event('input'))
  }

  handleValidation() {
    if (!this.filled) {
      this.success = false
      return
    }

    // TODO: Might need to make this more specific
    this.success = this.input.validity.valid
  }

  handleRevealPassword() {
    this.input.type = this.input.type === 'password' ? 'text' : 'password'
  }

  handleAutoResize(e) {
    if (!this.resize) return
    const { target } = e

    target.style.height = 0
    this.style.height = 0
    target.style.height = `${target.scrollHeight}px`
    this.style.height = `${target.scrollHeight + 26}px`
  }

  iconTemplate() {
    return html`
      <slot
        name="clear-icon"
        class="${classMap({ hide: !this.clearable })}"
        @click="${this.handleClear}"
      ></slot>
      <slot
        name="reveal-icon"
        class="${classMap({ hide: !this.revealPassword })}"
        @click="${this.handleRevealPassword}"
      ></slot>
    `
  }

  get input() {
    const slot = this.shadowRoot.querySelector(`slot[name='input']`)
    return slot?.assignedNodes()[0]
  }

  get errorSlot() {
    const slot = this.shadowRoot.querySelector(`slot[name='error']`)
    return slot?.assignedNodes()[0]
  }

  render() {
    return html`
      <slot name="icon" @click="${this.onIconClick}"></slot>
      <slot name="label"></slot>
      ${(this.icon === 'left' && this.iconTemplate()) || ''}
      <slot
        name="input"
        @change="${this.onChange}"
        @input="${this.onChange}"
        @animationstart="${this.onAutoFill}"
        @focusin="${this.onFocus}"
        @focusout="${this.onFocusOut}"
      ></slot>
      ${(this.icon === 'right' && this.iconTemplate()) || ''}
      <slot name="error"></slot>
      <slot name="hint"></slot>
    `
  }

  static styles = css`
    ::slotted(label) {
      position: absolute;
      top: 15px;
      transform: translateY(0);
      transition: transform var(--nova-duration-default, 300ms),
        font-size var(--nova-duration-default, 300ms);
    }

    ::slotted(label),
    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      width: 100%;
      position: absolute;
    }

    ::slotted(input),
    ::slotted(textarea),
    ::slotted(select) {
      z-index: 2;
      font: var(--nova-font);
      color: currentColor;
      background: transparent;
      border: 0;
      padding: 0;
      top: 15px;
    }

    ::slotted(input:-webkit-autofill) {
      animation-name: onAutoFillStart;
    }

    ::slotted(input:not(:-webkit-autofill)) {
      animation-name: onAutoFillCancel;
    }

    ::slotted(input:focus-visible),
    ::slotted(textarea:focus-visible) {
      outline: none !important;
    }

    ::slotted(select) {
      appearance: none;
    }

    :host([focused]) ::slotted(label),
    :host([filled]) ::slotted(label) {
      transform: translateY(-16px);
      transition: transform var(--nova-duration-default, 300ms),
        font-size var(--nova-duration-default, 300ms);
    }

    :host([animation='disappear'][focused]) ::slotted(label),
    :host([animation='disappear'][filled]) ::slotted(label) {
      visibility: hidden;
      transform: translateY(0);
      font-size: inherit;
    }

    :host {
      position: relative;
      display: block;
      height: var(--nova-height, 45px);
    }

    :host {
      background: transparent;
      border-bottom: var(--nova-border-size, 1px) solid currentColor;
      transition: border var(--nova-duration-default, 300ms);
    }

    :host([focused]),
    :host(:hover) {
      border-bottom: var(--nova-border-size-active, 1px) solid currentColor;
    }

    :host([focused]) {
      outline: 0.2rem solid var(--nova-color-focus);
    }

    :host([disabled]) {
      border: none;
      background-color: var(--nova-color-input-disabled, #f5f5f5);
    }

    :host([disabled]) ::slotted(label) {
      opacity: 0.4;
    }

    :host([resize]) ::slotted(textarea) {
      resize: none;
      background-image: var(--nova-textarea-svg);
      background-repeat: no-repeat;
      background-position: calc(100% - 0.2rem) calc(100% - 0.4rem);
    }

    :host([error]) {
      border-color: var(--nova-color-error, #f44336);
    }

    :host([theme='minimal']) {
      background: transparent;
      border: none;
      border-bottom: var(--nova-border-size, 1px) solid currentColor;
    }

    :host([label='stacked']) {
      border: none;
      height: auto;
    }

    :host([label='stacked']) ::slotted(label),
    :host([label='stacked']) ::slotted(input),
    :host([label='stacked']) ::slotted(textarea),
    :host([label='stacked']) ::slotted(select) {
      position: relative;
    }

    :host([label='stacked']) ::slotted(label) {
      height: auto;
      display: block;
      font: var(--nova-label-font);
      margin: var(--nova-label-margin);
      padding-left: 0;
    }

    :host([label='stacked']) ::slotted(input),
    :host([label='stacked']) ::slotted(textarea),
    :host([label='stacked']) ::slotted(select) {
      border: var(--nova-border-size, 1px) solid currentColor;
    }

    :host([icon='left']) ::slotted(label) {
      padding-left: var(--nova-left-indent, 26px);
    }

    :host([icon='left']) ::slotted(input) {
      text-indent: var(--nova-left-indent, 32px);
    }

    :host([icon='right']) ::slotted(label) {
      padding-right: var(--nova-right-indent, 32px);
    }

    ::slotted([slot='icon']),
    ::slotted([slot='clear-icon']),
    ::slotted([slot='reveal-icon']) {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      width: var(--nova-icon-width, 23px);
      height: var(--nova-height, 45px);
      z-index: 3;
      appearance: none;
      background-color: transparent;
      border: none;
      padding: 0;
      color: currentColor;
      top: 0;
    }

    ::slotted([slot='icon']),
    ::slotted([slot='clear-icon']),
    ::slotted([slot='reveal-icon']) {
      cursor: pointer;
    }

    :host([icon='right']) ::slotted([slot='icon']),
    :host([icon='right']) ::slotted([slot='clear-icon']),
    :host([icon='right']) ::slotted([slot='reveal-icon']) {
      right: var(--nova-icon-right, 10px);
    }

    :host([icon='right'][clearable][reveal-password])
      ::slotted([slot='clear-icon']) {
      right: var(--nova-icon-clear-right, 33px);
    }

    :host(:not([filled])) ::slotted([slot='clear-icon']),
    :host(:not([filled])) ::slotted([slot='reveal-icon']) {
      display: none;
    }

    :host([validate]) ::slotted([slot='icon']),
    :host([validate]) ::slotted([slot='clear-icon']),
    :host([validate]) ::slotted([slot='reveal-icon']) {
      display: none;
    }

    :host([validate][success]) ::slotted([slot='icon']),
    :host([validate][success]) ::slotted([slot='clear-icon']),
    :host([validate][success]) ::slotted([slot='reveal-icon']) {
      display: flex;
    }

    :host([height='multiline']),
    :host([height='multiline']) ::slotted(textarea) {
      height: var(--nova-multiline-height, 100px);
    }

    :host([height='compact']) {
      height: var(--nova-compact-height, 65px);
    }

    :host([height='compact']) ::slotted(input),
    :host([height='compact']) ::slotted(label),
    :host([height='compact']) ::slotted([slot='icon']) {
      height: var(--nova-compact-height, 35px);
    }

    :host([height='compact']) ::slotted([slot='icon']) {
      width: 16px;
    }

    :host([height='compact']) ::slotted(label) {
      line-height: var(--nova-compact-height, 35px);
    }

    ::slotted([slot='hint']),
    ::slotted([slot='error']) {
      position: absolute;
      inset: 100% 0 0;
      margin-top: var(--nova-hint-margin-top, 0.8rem);
      font: var(--nova-hint-font);
      translate: var(--nova-hint-translate);
    }

    ::slotted([slot='error']) {
      color: var(--nova-color-error, var(--nova-color-danger, #ff0000));
    }

    .hide {
      display: none;
    }

    ::host button[type='button'] {
      padding: 0;
    }
  `
}

customElements.define('nova-input-animation-wrapper', NovaInputAnimationWrapper)
